import { Table, Text, useToggle } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'

import EmployeeCell from 'modules/audit_log/pages/settings/AuditLogPage/_Table/_EmployeeCell'
import type { Audit } from 'modules/audit_log/pages/settings/AuditLogPage/_Table/constants'
import type { AuditLogAccess } from 'modules/audit_log/pages/settings/AuditLogPage'

import EventData from './eventData'
import logo from './factorial-logo.svg'

interface Props {
  event: Audit
  totalSpan: number
  accesses: AuditLogAccess[]
}

const EventRow = ({ event, totalSpan, accesses }: Props) => {
  const [open, toggle] = useToggle(false)
  const { date, access_id, data, event_name } = event

  const getAccess = (id: string): AuditLogAccess | undefined => {
    return accesses.find((access) => access.id === id)
  }

  const getTarget = (): AuditLogAccess | undefined => {
    const accessId = data.snapshot?.access_id

    if (accessId) {
      return getAccess(accessId)
    }

    const employeeId = data.snapshot?.employee_id

    if (employeeId) {
      return accesses.find((access) => access.employeeId === employeeId)
    }
  }

  const author = getAccess(access_id)
  const target = getTarget()
  const fullDate = new Date(date)
  const dateFormatted = moment(fullDate).format('YYYY-MM-DD HH:mm').toString()

  const getName = (access: AuditLogAccess | undefined) => {
    return access
      ? getFullDisplayName({
          firstName: access.firstName,
          lastName: access.lastName,
        })
      : i18n.t('settings.audit_log.log_detail.factorial_system')
  }

  const getAvatar = (access: AuditLogAccess | undefined) => {
    return access ? access.avatar : logo
  }

  const becomeId = event.become_id
  const becomeAccess = becomeId ? getAccess(becomeId) : undefined
  const { has_become_enabled: becomeEnabled } = useFeatures(['become_enabled'])

  const content = () => {
    return (
      <>
        <Table.Cell colSpan={5}>
          <EmployeeCell url={getAvatar(author) ?? ''} name={getName(author)} />
        </Table.Cell>
        <Table.Cell colSpan={5}>
          <Text.Normal weight="medium">{event_name}</Text.Normal>
        </Table.Cell>
        {becomeEnabled && (
          <Table.Cell colSpan={5}>
            {event.become_id ? (
              <EmployeeCell url={getAvatar(becomeAccess) ?? ''} name={getName(becomeAccess)} />
            ) : (
              <Text.Normal weight="medium">---</Text.Normal>
            )}
          </Table.Cell>
        )}
        <Table.Cell colSpan={5}>
          {target ? (
            <EmployeeCell url={getAvatar(target) ?? ''} name={getName(target)} />
          ) : (
            <Text.Normal weight="medium">---</Text.Normal>
          )}
        </Table.Cell>
        <Table.Cell colSpan={5}>
          <Text.Normal weight="medium">{dateFormatted}</Text.Normal>
        </Table.Cell>
      </>
    )
  }

  return (
    <Table.ExpandableRow
      content={content}
      toggle={toggle(!open)}
      opened={open}
      iconColSpan={2}
      icon="chevron"
      iconPosition="right"
    >
      <Table.Row>
        <Table.Cell colSpan={totalSpan} isExplotable>
          <EventData data={data} />
        </Table.Cell>
      </Table.Row>
    </Table.ExpandableRow>
  )
}

export default EventRow
